import { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { EditableStudentField, FIELD_LABELS } from '../../types/portal';

interface RequestChangeDialogProps {
  open: boolean;
  fieldName: EditableStudentField | null;
  currentValue: string;
  errorMessage: string | null;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (newValue: string) => void;
}

/** Student Portal (Phase 67) — the ONLY way a student can propose a
 * change to their own record; never a direct edit. Submitting creates a
 * PENDING StudentChangeRequest reviewed by their Counselor — the official
 * record is untouched until approved. */
export const RequestChangeDialog = ({
  open,
  fieldName,
  currentValue,
  errorMessage,
  submitting,
  onClose,
  onConfirm,
}: RequestChangeDialogProps) => {
  const [newValue, setNewValue] = useState('');

  useEffect(() => {
    if (open) setNewValue(currentValue);
  }, [open, currentValue]);

  const label = fieldName ? FIELD_LABELS[fieldName] : '';
  const isDate = fieldName === 'passportExpiryDate';
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  // Same rules as the server (studentPortal.service.ts normalizeChangeValue).
  const validationError =
    !newValue.trim()
      ? null
      : fieldName === 'passportNumber' && !/^[A-Z0-9]{6,20}$/.test(newValue.toUpperCase().replace(/\s+/g, ''))
        ? 'Use 6–20 letters and digits, with no symbols.'
        : isDate && newValue < tomorrow
          ? 'That passport would already be expired — enter the expiry date of a valid passport.'
          : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Request a change</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Current {label.toLowerCase()}:{' '}
            {currentValue ? (isDate ? new Date(`${currentValue}T00:00:00Z`).toLocaleDateString() : currentValue) : '—'}
          </Typography>
          <TextField
            label={`New ${label.toLowerCase()}`}
            size="small"
            fullWidth
            autoFocus
            type={isDate ? 'date' : 'text'}
            InputLabelProps={isDate ? { shrink: true } : undefined}
            inputProps={isDate ? { min: tomorrow } : undefined}
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            error={!!validationError}
            helperText={validationError ?? (fieldName === 'passportNumber' ? 'As printed on your passport, e.g. N1234567' : ' ')}
          />
          <Typography variant="caption" color="text.secondary">
            This change won&apos;t take effect until your Counselor reviews and approves it.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={submitting || !newValue.trim() || !!validationError}
          onClick={() => onConfirm(newValue.trim())}
        >
          {submitting ? 'Submitting…' : 'Submit request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
